import { COLORS } from "../shared/constants";
import { IconBack } from "../shared/icons";
import { useState, useEffect, useRef } from "react";
import * as api from "../api";

export default // ─── Device Control Screen ─────────────────────────────────
// Standalone device testing and calibration. No patient context.
// No MT recording, no session logging. Hardware testing only.
function DeviceControlScreen({ currentUser, onNavigate, protocols }) {
  const [connected, setConnected] = useState(false);
  const [simulated, setSimulated] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const [amplitude, setAmplitude] = useState(50);
  const [amplitudeInput, setAmplitudeInput] = useState("50");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(null);
  const [armed, setArmed] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState("");
  const [protocolLoading, setProtocolLoading] = useState(false);
  const [loadedProtocol, setLoadedProtocol] = useState(null);
  const [running, setRunning] = useState(false);
  // Custom stim parameters
  const [customType, setCustomType] = useState("RTMS");
  const [customFreq, setCustomFreq] = useState("10");
  const [customTrainPulses, setCustomTrainPulses] = useState("40");
  const [customTrains, setCustomTrains] = useState("75");
  const [customITI, setCustomITI] = useState("26");
  const [customBurstPulses, setCustomBurstPulses] = useState("3");
  const [customIPI, setCustomIPI] = useState("20");
  const [customIPIUnit, setCustomIPIUnit] = useState("ms");
  const [customLoading, setCustomLoading] = useState(false);
  const [customDirty, setCustomDirty] = useState(false);
  const pollRef = useRef(null);

  const activeProtocols = (protocols || []).filter(p => p.is_active !== false);

  // Check connection on mount
  useEffect(() => {
    api.deviceStatus().then((s) => {
      setConnected(s.connected || false);
      setSimulated(s.simulated || false);
      setStatus(s);
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Telemetry polling when connected
  useEffect(() => {
    if (!connected) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const poll = () => {
      api.deviceTelemetry().then((t) => {
        setTelemetry(t);
        setArmed(t.device_enabled || false);
        setEvents(t.events || []);
      }).catch(() => {});
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [connected]);

  const handleConnect = async () => {
    setLoading(true); setError(null);
    try {
      const result = await api.deviceConnect();
      setConnected(result.success);
      setSimulated(result.simulated || false);
      if (!result.success) setError(result.message);
    } catch (err) { setError(err.error || "Connection failed"); }
    setLoading(false);
  };

  const handleDisconnect = async () => {
    try {
      await api.deviceDisconnect();
      setConnected(false); setTelemetry(null); setStatus(null); setArmed(false);
      setLoadedProtocol(null); setRunning(false);
    } catch (err) { setError(err.error || "Disconnect failed"); }
  };

  const handleAmplitudeChange = async (val) => {
    const v = Math.max(0, Math.min(100, val));
    setAmplitude(v);
    setAmplitudeInput(String(v));
    if (connected) {
      try { await api.deviceSetAmplitude(v); } catch (err) { console.warn(err); }
    }
  };

  const handleAmplitudeInputCommit = () => {
    const parsed = parseInt(amplitudeInput, 10);
    if (!isNaN(parsed)) {
      handleAmplitudeChange(parsed);
    } else {
      setAmplitudeInput(String(amplitude));
    }
  };

  const handleUseDeviceAmplitude = () => {
    if (telemetry?.amplitude_pct != null) {
      setAmplitude(telemetry.amplitude_pct);
      setAmplitudeInput(String(telemetry.amplitude_pct));
    }
  };

  const handleToggleArm = async () => {
    setError(null);
    try {
      if (armed) {
        await api.deviceDisarm();
        setArmed(false);
      } else {
        await api.deviceArm();
        setArmed(true);
      }
    } catch (err) { setError(err.error || "Arm/Disarm failed"); }
  };

  const handleFire = async () => {
    setError(null);
    try {
      // Force-release any stale device lock — Device Control is an admin
      // testing screen, not a patient session. Locks from abandoned sessions
      // should not block hardware testing.
      await api.deviceReleaseLock(null).catch(() => {});
      if (!armed) {
        await api.deviceArm();
        setArmed(true);
        // Brief delay to let MagPro arm before firing
        await new Promise(r => setTimeout(r, 300));
      }
      await api.deviceFire();
    } catch (err) { setError(err.error || "Fire failed"); }
  };

  const handleLoadProtocol = async () => {
    if (!selectedProtocol || !connected) return;
    setProtocolLoading(true); setError(null);
    try {
      const result = await api.deviceLoadProtocolDirect(selectedProtocol, amplitude || 0);
      if (!result.success) {
        setError(result.error || "Load failed");
      } else {
        const p = activeProtocols.find(pr => pr.id === selectedProtocol);
        setLoadedProtocol(p || null);
        // Populate Stimulation Parameters fields from loaded protocol
        if (p) {
          setCustomType(p.treatment_type === "TBS" ? "TBS" : "RTMS");
          setCustomFreq(String(p.frequency_hz ?? "10"));
          setCustomTrainPulses(String(p.train_pulses ?? "40"));
          setCustomTrains(String(p.num_trains ?? "75"));
          setCustomITI(String(p.inter_train_interval_sec ?? "26"));
          if (p.treatment_type === "TBS") {
            setCustomBurstPulses(String(p.pulses_in_burst ?? "3"));
            setCustomIPI(String(p.inter_pulse_interval_ms ?? "20"));
            setCustomIPIUnit("ms");
          }
          setCustomDirty(false);
        }
      }
    } catch (err) { setError(err.error || "Protocol load failed"); }
    setProtocolLoading(false);
  };

  const handleLoadCustomParams = async () => {
    if (!connected) return;
    setCustomLoading(true); setError(null);
    try {
      let ipiMs = parseFloat(customIPI);
      if (customIPIUnit === "Hz" && ipiMs > 0) {
        ipiMs = Math.round(1000 / ipiMs);
      }
      const params = {
        treatment_type: customType,
        frequency_hz: parseFloat(customFreq),
        train_pulses: parseInt(customTrainPulses, 10),
        num_trains: parseInt(customTrains, 10),
        inter_train_interval_sec: parseFloat(customITI),
        intensity_pct: amplitude,
      };
      if (customType === "TBS") {
        params.pulses_in_burst = parseInt(customBurstPulses, 10);
        params.inter_pulse_interval_ms = ipiMs;
      }
      const result = await api.deviceLoadCustomParams(params);
      if (!result.success) {
        setError(result.error || "Load failed");
      } else {
        const totalPulses = customType === "TBS"
          ? parseInt(customBurstPulses, 10) * parseInt(customTrainPulses, 10) * parseInt(customTrains, 10)
          : parseInt(customTrainPulses, 10) * parseInt(customTrains, 10);
        setLoadedProtocol({
          name: "Custom Parameters",
          treatment_type: customType,
          frequency_hz: parseFloat(customFreq),
          train_pulses: parseInt(customTrainPulses, 10),
          num_trains: parseInt(customTrains, 10),
          inter_train_interval_sec: parseFloat(customITI),
          total_pulses: totalPulses,
          pulses_in_burst: parseInt(customBurstPulses, 10),
          inter_pulse_interval_ms: ipiMs,
        });
        setCustomDirty(false);
      }
    } catch (err) { setError(err.error || "Custom params load failed"); }
    setCustomLoading(false);
  };

  const handleRunProtocol = async () => {
    if (!connected || !loadedProtocol) return;
    setError(null); setRunning(true);
    try {
      // Force-release any stale lock from abandoned sessions
      await api.deviceReleaseLock(null).catch(() => {});
      if (!armed) {
        await api.deviceArm();
        setArmed(true);
      }
      await api.deviceSetAmplitude(amplitude);
      await api.deviceStartTreatment();
    } catch (err) {
      setError(err.error || "Run failed");
      setRunning(false);
    }
  };

  const handleStopProtocol = async () => {
    setError(null);
    try {
      await api.deviceStopTreatment();
      await api.deviceDisarm().catch(() => {});
    } catch (err) { setError(err.error || "Stop failed"); }
    setRunning(false);
  };

  const tempColor = telemetry?.coil_temp_status === "red" ? "#E74C3C" : telemetry?.coil_temp_status === "yellow" ? "#F39C12" : "#2ECC71";

  const labelSt = { fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" };
  const inputSt = { width: "100%", height: "36px", padding: "0 10px", borderRadius: "8px", border: `1.5px solid ${COLORS.border}`, background: COLORS.inputBg, fontSize: "13px", color: COLORS.text, boxSizing: "border-box" };
  const sectionHeader = { fontSize: "12px", fontWeight: "700", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <button onClick={() => onNavigate("HOME")} style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textSecondary }}><IconBack /></button>
        <div>
          <h2 style={{ color: COLORS.text, fontSize: "20px", fontWeight: "600", margin: 0 }}>Device Control</h2>
          <div style={{ fontSize: "12px", color: COLORS.textSecondary, marginTop: "2px" }}>Hardware testing & calibration only — no session logging</div>
        </div>
      </div>

      {error && (
        <div style={{ padding: "12px 16px", background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "10px", marginBottom: "16px", fontSize: "13px", color: COLORS.danger }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: "12px", background: "none", border: "none", color: COLORS.danger, cursor: "pointer", fontWeight: "600" }}>✕</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        {/* Left: Controls */}
        <div>
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px", marginBottom: "16px" }}>

            {/* Amplitude: − [input] + with slider below */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary }}>Amplitude</label>
                {connected && telemetry?.amplitude_pct != null && telemetry.amplitude_pct !== amplitude && (
                  <button onClick={handleUseDeviceAmplitude}
                    style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "6px", border: `1px solid ${COLORS.primary}`, background: COLORS.primaryLight, color: COLORS.primary, fontWeight: "600", cursor: "pointer" }}>
                    Use device ({telemetry.amplitude_pct}%)
                  </button>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "10px" }}>
                <button onClick={() => handleAmplitudeChange(amplitude - 1)}
                  style={{ width: "40px", height: "40px", borderRadius: "10px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "22px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  −
                </button>
                <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={amplitudeInput}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      setAmplitudeInput(v);
                    }}
                    onBlur={handleAmplitudeInputCommit}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAmplitudeInputCommit(); }}
                    style={{
                      width: "80px", textAlign: "center", fontSize: "36px", fontWeight: "800",
                      color: COLORS.text, background: "transparent", border: "none", outline: "none",
                      borderBottom: `2px solid ${COLORS.border}`, padding: "0 4px",
                    }}
                  />
                  <span style={{ fontSize: "24px", fontWeight: "700", color: COLORS.textSecondary, marginLeft: "2px" }}>%</span>
                </div>
                <button onClick={() => handleAmplitudeChange(amplitude + 1)}
                  style={{ width: "40px", height: "40px", borderRadius: "10px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "22px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  +
                </button>
              </div>
              <input type="range" min="0" max="100" value={amplitude}
                onChange={(e) => handleAmplitudeChange(parseInt(e.target.value))}
                style={{ width: "100%", accentColor: COLORS.primary }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: COLORS.textSecondary, marginTop: "2px" }}>
                <span>0%</span><span>50%</span><span>100%</span>
              </div>
            </div>

            {/* Arm/Disarm toggle + Fire — single row */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
              <button onClick={handleToggleArm}
                style={{ flex: 1, height: "44px", borderRadius: "8px", border: armed ? "2px solid #2ECC71" : `2px solid ${COLORS.border}`,
                  background: armed ? "rgba(46,204,113,0.1)" : COLORS.surface,
                  color: armed ? "#2ECC71" : COLORS.textSecondary, fontSize: "13px", fontWeight: "700", cursor: "pointer" }}>
                {armed ? "● Enabled — Disarm" : "○ Arm"}
              </button>
              <button onClick={handleFire}
                style={{ flex: 1, height: "44px", borderRadius: "8px", border: "none", background: "#E74C3C", color: "#FFF", fontSize: "13px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                ⚡ Fire
              </button>
            </div>

            {/* Protocol Loader */}
            <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: "16px" }}>
              <div style={sectionHeader}>Load Protocol</div>
              <div style={{ display: "flex", gap: "8px" }}>
                <select value={selectedProtocol} onChange={(e) => setSelectedProtocol(e.target.value)}
                  style={{ flex: 1, height: "40px", padding: "0 10px", borderRadius: "8px", border: `1.5px solid ${COLORS.border}`, background: COLORS.inputBg, fontSize: "13px", color: COLORS.text }}>
                  <option value="">Select a protocol…</option>
                  {activeProtocols.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.treatment_type === "TBS" ? "TBS" : p.treatment_type === "MPNS" ? "mPNS" : "rTMS"})
                    </option>
                  ))}
                </select>
                <button onClick={handleLoadProtocol} disabled={!selectedProtocol || protocolLoading}
                  style={{ height: "40px", padding: "0 16px", borderRadius: "8px", border: "none",
                    background: selectedProtocol ? COLORS.primary : COLORS.border,
                    color: "#FFF", fontSize: "12px", fontWeight: "700",
                    cursor: selectedProtocol ? "pointer" : "default",
                    opacity: selectedProtocol ? 1 : 0.5 }}>
                  {protocolLoading ? "Loading…" : "Load"}
                </button>
              </div>
              {selectedProtocol && (() => {
                const p = activeProtocols.find(pr => pr.id === selectedProtocol);
                if (!p) return null;
                return (
                  <div style={{ marginTop: "8px", fontSize: "11px", color: COLORS.textSecondary, lineHeight: 1.6 }}>
                    {p.frequency_hz}Hz · {p.train_pulses}p × {p.num_trains}t · {p.inter_train_interval_sec}s ITI · {p.total_pulses?.toLocaleString()} pulses
                    {p.treatment_type === "TBS" && ` · ${p.pulses_in_burst || 3} burst`}
                  </div>
                );
              })()}
            </div>

            {/* Custom Stimulation Parameters */}
            <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: "16px", marginTop: "16px" }}>
              <div style={sectionHeader}>Stimulation Parameters</div>

              {/* rTMS / TBS toggle */}
              <div style={{ display: "flex", gap: "0", marginBottom: "14px", borderRadius: "8px", overflow: "hidden", border: `1.5px solid ${COLORS.border}` }}>
                {["RTMS", "TBS"].map(t => (
                  <button key={t} onClick={() => { setCustomType(t); setCustomDirty(true); }}
                    style={{
                      flex: 1, height: "36px", border: "none", fontSize: "12px", fontWeight: "700", cursor: "pointer",
                      background: customType === t ? COLORS.primary : COLORS.surface,
                      color: customType === t ? "#FFF" : COLORS.textSecondary,
                    }}>
                    {t === "RTMS" ? "rTMS" : "TBS"}
                  </button>
                ))}
              </div>

              {/* Parameter fields */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <div style={labelSt}>Frequency (Hz)</div>
                  <input type="text" inputMode="decimal" value={customFreq} onChange={(e) => { setCustomFreq(e.target.value); setCustomDirty(true); }} style={inputSt} />
                </div>
                <div>
                  <div style={labelSt}>Train Pulses</div>
                  <input type="text" inputMode="numeric" value={customTrainPulses} onChange={(e) => { setCustomTrainPulses(e.target.value); setCustomDirty(true); }} style={inputSt} />
                </div>
                <div>
                  <div style={labelSt}># of Trains</div>
                  <input type="text" inputMode="numeric" value={customTrains} onChange={(e) => { setCustomTrains(e.target.value); setCustomDirty(true); }} style={inputSt} />
                </div>
                <div>
                  <div style={labelSt}>ITI (sec)</div>
                  <input type="text" inputMode="decimal" value={customITI} onChange={(e) => { setCustomITI(e.target.value); setCustomDirty(true); }} style={inputSt} />
                </div>

                {/* TBS-only fields */}
                {customType === "TBS" && (
                  <>
                    <div>
                      <div style={labelSt}>Pulses in Burst</div>
                      <input type="text" inputMode="numeric" value={customBurstPulses} onChange={(e) => { setCustomBurstPulses(e.target.value); setCustomDirty(true); }} style={inputSt} />
                    </div>
                    <div>
                      <div style={{ ...labelSt, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Inter-Pulse Interval</span>
                        <div style={{ display: "flex", gap: "0", borderRadius: "4px", overflow: "hidden", border: `1px solid ${COLORS.border}` }}>
                          {["ms", "Hz"].map(u => (
                            <button key={u} onClick={() => { setCustomIPIUnit(u); setCustomDirty(true); }}
                              style={{
                                padding: "1px 6px", border: "none", fontSize: "10px", fontWeight: "600", cursor: "pointer",
                                background: customIPIUnit === u ? COLORS.primary : "transparent",
                                color: customIPIUnit === u ? "#FFF" : COLORS.textSecondary,
                              }}>
                              {u}
                            </button>
                          ))}
                        </div>
                      </div>
                      <input type="text" inputMode="decimal" value={customIPI} onChange={(e) => { setCustomIPI(e.target.value); setCustomDirty(true); }}
                        style={inputSt}
                        placeholder={customIPIUnit === "ms" ? "e.g. 20" : "e.g. 50"} />
                    </div>
                  </>
                )}
              </div>

              {/* Calculated total */}
              {(() => {
                const tp = parseInt(customTrainPulses, 10) || 0;
                const nt = parseInt(customTrains, 10) || 0;
                const bp = parseInt(customBurstPulses, 10) || 0;
                const total = customType === "TBS" ? bp * tp * nt : tp * nt;
                return total > 0 ? (
                  <div style={{ marginTop: "10px", fontSize: "12px", color: COLORS.textSecondary }}>
                    Total pulses: <span style={{ fontWeight: "600", color: COLORS.text }}>{total.toLocaleString()}</span>
                  </div>
                ) : null;
              })()}

              <button onClick={handleLoadCustomParams} disabled={!connected || !customDirty || customLoading}
                style={{
                  width: "100%", height: "40px", borderRadius: "8px", border: "none", marginTop: "12px",
                  background: connected && customDirty ? COLORS.primary : COLORS.border,
                  color: "#FFF", fontSize: "12px", fontWeight: "700",
                  cursor: connected && customDirty ? "pointer" : "default",
                  opacity: connected && customDirty ? 1 : 0.5,
                }}>
                {customLoading ? "Loading…" : "Load Custom Parameters"}
              </button>
            </div>

            {/* Loaded Protocol Display + Run */}
            {loadedProtocol && (
              <div style={{ marginTop: "16px", borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: "16px" }}>
                <div style={sectionHeader}>Loaded on MagPro</div>
                <div style={{
                  padding: "14px", background: "rgba(46,204,113,0.06)", border: "1px solid rgba(46,204,113,0.2)",
                  borderRadius: "8px", marginBottom: "12px",
                }}>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text, marginBottom: "6px" }}>
                    {loadedProtocol.name}
                    <span style={{
                      marginLeft: "8px", padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "600",
                      background: loadedProtocol.treatment_type === "TBS" ? "#EDE7F6" : loadedProtocol.treatment_type === "MPNS" ? "#FEF3C7" : COLORS.primaryLight,
                      color: loadedProtocol.treatment_type === "TBS" ? "#6A3DB5" : loadedProtocol.treatment_type === "MPNS" ? "#92400E" : COLORS.primary,
                    }}>
                      {loadedProtocol.treatment_type === "TBS" ? "TBS" : loadedProtocol.treatment_type === "MPNS" ? "mPNS" : "rTMS"}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: COLORS.textSecondary, lineHeight: 1.8 }}>
                    <div>Frequency: {loadedProtocol.frequency_hz} Hz</div>
                    <div>Pulses/train: {loadedProtocol.train_pulses} · Trains: {loadedProtocol.num_trains} · ITI: {loadedProtocol.inter_train_interval_sec}s</div>
                    <div>Total pulses: {loadedProtocol.total_pulses?.toLocaleString()}</div>
                    {loadedProtocol.treatment_type === "TBS" && <div>Burst pulses: {loadedProtocol.pulses_in_burst || 3} · IPI: {loadedProtocol.inter_pulse_interval_ms}ms</div>}
                    <div>Amplitude: {amplitude}%</div>
                  </div>
                </div>
                {!running ? (
                  <button onClick={handleRunProtocol} disabled={!connected}
                    style={{
                      width: "100%", height: "48px", borderRadius: "8px", border: "none",
                      background: connected ? "#2ECC71" : COLORS.border,
                      color: "#FFF", fontSize: "14px", fontWeight: "700",
                      cursor: connected ? "pointer" : "default",
                      opacity: connected ? 1 : 0.5,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    }}>
                    ▶ Run Protocol
                  </button>
                ) : (
                  <button onClick={handleStopProtocol}
                    style={{
                      width: "100%", height: "48px", borderRadius: "8px", border: "none",
                      background: "#E74C3C", color: "#FFF", fontSize: "14px", fontWeight: "700",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    }}>
                    ■ Stop
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: Telemetry + Connection + Events */}
        <div>
          {/* Telemetry */}
          <div style={{ background: "#1A1F2E", borderRadius: "12px", padding: "20px", marginBottom: "16px", color: "rgba(255,255,255,0.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Telemetry</div>
              {connected && <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "4px", background: simulated ? "rgba(52,152,219,0.2)" : "rgba(46,204,113,0.2)", color: simulated ? "#3498DB" : "#2ECC71" }}>{simulated ? "Simulated" : "Live"}</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Coil Temp</div>
                <div style={{ fontSize: "24px", fontWeight: "700", color: connected && telemetry ? tempColor : "rgba(255,255,255,0.3)" }}>
                  {connected && telemetry ? (telemetry.no_coil_flow ? "No Flow" : telemetry.coil_temp_c != null ? `${telemetry.coil_temp_c}°C` : "--°C") : "--°C"}
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Amplitude</div>
                <div style={{ fontSize: "24px", fontWeight: "700", color: connected && telemetry ? "#FFF" : "rgba(255,255,255,0.3)" }}>
                  {connected && telemetry?.amplitude_pct != null ? `${telemetry.amplitude_pct}%` : "--%"}
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Status</div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: connected ? (telemetry?.device_enabled ? "#2ECC71" : "#3498DB") : "#E74C3C" }} />
                  <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}>
                    {connected ? (telemetry?.device_enabled ? "Enabled" : "Disabled") : "Offline"}
                  </span>
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Coil Type</div>
                <div style={{ fontSize: "13px", color: connected && telemetry?.coil_type ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)" }}>
                  {telemetry?.coil_type || "—"}
                </div>
              </div>
            </div>
            {connected && telemetry && (
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>
                {telemetry.mode && <span>Mode: {telemetry.mode} · </span>}
                {telemetry.waveform && <span>Waveform: {telemetry.waveform}</span>}
              </div>
            )}
          </div>

          {/* Connection — below Telemetry */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px", marginBottom: "16px" }}>
            <div style={sectionHeader}>Connection</div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
              <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: connected ? "#2ECC71" : "#E74C3C" }} />
              <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text }}>
                {connected ? (simulated ? "Connected (Simulated)" : "Connected") : "Disconnected"}
              </span>
            </div>
            {!connected ? (
              <button onClick={handleConnect} disabled={loading}
                style={{ width: "100%", height: "44px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "14px", fontWeight: "700", cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1 }}>
                {loading ? "Connecting…" : "Connect to MagPro"}
              </button>
            ) : (
              <button onClick={handleDisconnect}
                style={{ width: "100%", height: "38px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                Disconnect
              </button>
            )}
          </div>

          {/* Force Unlock — clears orphaned device lock */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: COLORS.text }}>Device Lock</div>
                <div style={{ fontSize: "11px", color: COLORS.textSecondary, marginTop: "2px" }}>
                  Clear if "MagPro In Use" persists after a session ends
                </div>
              </div>
              <button onClick={async () => {
                  try {
                    await api.deviceReleaseLock(null);
                    setError(null);
                    setEvents(prev => [...prev, { timestamp: new Date().toISOString(), message: "Device lock force-released" }]);
                  } catch (err) {
                    setError(err.error || "Unlock failed");
                  }
                }}
                style={{ height: "34px", padding: "0 16px", borderRadius: "8px", border: `1px solid ${COLORS.warningBorder}`, background: COLORS.warningLight, color: COLORS.warning, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                Force Unlock
              </button>
            </div>
          </div>

          {/* Event Log */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px" }}>
            <div style={sectionHeader}>Event Log</div>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {events.length === 0 ? (
                <div style={{ fontSize: "13px", color: COLORS.textSecondary, opacity: 0.5 }}>No events yet</div>
              ) : (
                events.slice().reverse().map((evt, i) => (
                  <div key={i} style={{ fontSize: "12px", color: COLORS.textSecondary, padding: "4px 0", borderBottom: `1px solid ${COLORS.borderLight}`, fontFamily: "monospace" }}>
                    <span style={{ color: COLORS.text, opacity: 0.4 }}>{evt.timestamp?.split("T")[1]?.substring(0, 8)}</span>{" "}
                    {evt.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
